import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import { detectLanguage } from '../../common/utils';
import { getSolrData } from '../../../hooks/useSolr';
import { useInfiniteQuery } from 'react-query';
import { Form, InputGroup } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { BsSearch } from 'react-icons/all';

/**
 * Show Searchbox with suggestion list as one types
 *
 * @returns {JSX.Element}
 * @constructor
 */
export default function TermsGlossarySearchBox() {
    // const history = useHistory();
    const bogusquery = '!nothing';
    const { dictionaries, setStartAt, setSearchTerm } = useDictionaryStore();
    const [query, setQuery] = useState(bogusquery);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [noneFound, setNoneFound] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const queryfield = detectLanguage(query) === 'sa' ? 'name_deva' : `header`;

    let seldicts = Object.keys(
        Object.fromEntries(
            Object.entries(dictionaries).filter(([_, v]) => v.selected)
        )
    ).join(' OR ');

    if (seldicts.length === 0) {
        seldicts = 'NoDictionary';
    }

    // &fq={!parent which=block_type:parent}related_definitions_source_code_s:(kolver OR manandhar OR jørgensen)
    const getSuggestions = async ({ pageParam }) => {
        // console.log('*** pg in getsuggestions', pageParam);
        if (!pageParam) {
            pageParam = 0;
        }
        const suggestionListQuery = {
            index: 'terms',
            params: {
                q: `${queryfield}:("${query}" OR ${query}*)`,
                fq: [
                    `tree:terms`,
                    '-associated_subject_ids:10524',
                    'ancestors_new.alpha:*',
                    `{!parent which=block_type:parent}related_definitions_source_code_s:(${seldicts})`,
                ],
                start: pageParam * 100,
                rows: 100,
                fl: '*,[child parentFilter=block_type:parent childFilter=-block_child_type:places_shape limit=1000]',
                facet: false,
                sort: `score DESC, cascading_position_i ASC`,
            },
        };
        return getSolrData(suggestionListQuery, false);
    };
    const {
        data: suggestionData,
        error: suggestionError,
        fetchNextPage, // a comment here
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ['suggestion-list', query],
        queryFn: getSuggestions,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            // console.log('last page in get next', lastPage, pages);
            // console.log('returning', lastPage.start / 100 + 1);
            return lastPage.start / 100 + 1;
        },
    });

    const loading = isFetching || isFetchingNextPage;

    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;

            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((prev) =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;

            case 'Enter':
                if (activeIndex >= 0) {
                    console.log(
                        'handling selected suggestion',
                        activeIndex,
                        suggestions
                    );
                    handleSelectSuggestion(suggestions[activeIndex]);
                }
                break;

            case 'Escape':
                // setActiveIndex(-1);
                setShowSuggestions(false);
                break;

            default:
                break;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const inel = document.getElementById('glossary-search-input');
        if (inel?.value) {
            setSearchTerm(inel.value);
        }
    };

    const handleSelectSuggestion = (suggestion) => {
        //setShowSuggestions(false);
        const tid = suggestion.uid.replace('terms-', '');
        setSearchTerm('');
        setStartAt(tid);
    };

    useEffect(() => {
        const myClick = (event) => {
            setShowSuggestions(false);
        };
        document.addEventListener('click', myClick);
        const myKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('keydown', myKeyDown);
        return () => {
            document.removeEventListener('keydown', myKeyDown);
            document.removeEventListener('click', myClick);
        };
    }, []);

    useEffect(() => {
        const listEl = document.getElementById('suggestion-list');
        if (listEl && showSuggestions) {
            const activeEl = listEl.querySelector('li.active');
            if (!activeEl) return;

            const itemTop = activeEl.offsetTop;
            const itemBottom = itemTop + activeEl.offsetHeight;

            const listTop = listEl.scrollTop;
            const listBottom = listTop + listEl.clientHeight;

            if (itemTop < listTop) {
                listEl.scrollTop = itemTop;
            } else if (itemBottom > listBottom) {
                listEl.scrollTop = itemBottom - listEl.clientHeight;
            }
        }
    }, [activeIndex, showSuggestions]);

    useEffect(() => {
        if (typeof query !== 'string' || query === '') {
            setQuery(bogusquery);
        }
    }, [query]);

    useEffect(() => {
        const flatSuggestions = suggestionData?.pages
            ? suggestionData?.pages.flatMap((page) => page.docs)
            : [];
        setSuggestions(flatSuggestions);
        setActiveIndex(-1);
        if (flatSuggestions?.length > 0) {
            setNoneFound(false);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
            if (query !== bogusquery && !loading) {
                setNoneFound(true);
            } else {
                setNoneFound(false);
            }
        }
    }, [suggestionData, query, loading]);

    const numFound =
        suggestionData && suggestionData?.pages?.length > 0
            ? suggestionData.pages[0].numFound * 1
            : 0;
    const lastFound =
        suggestionData && suggestionData?.pages?.length > 0
            ? suggestionData.pages[suggestionData.pages.length - 1].start * 1 +
              100
            : 1;

    // console.log('suggestions', suggestions);

    return (
        <div className="position-relative">
            <Form className="glossary-search-form p-2" onSubmit={handleSubmit}>
                <InputGroup>
                    <div className="glossary-input-wrapper">
                        <Form.Control
                            type="text"
                            value={query !== bogusquery ? query : ''}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Search..."
                            className="gloss-search"
                            id="glossary-search-input"
                            aria-label="Search glossary"
                        />
                        {query && query !== bogusquery && (
                            <button
                                type="button"
                                className="glossary-clear"
                                onClick={() => {
                                    setQuery('');
                                    setSearchTerm('');
                                }}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <Button
                        variant="secondary"
                        type="submit"
                        className="glossary-search-button"
                        aria-label="Submit search"
                    >
                        <BsSearch />
                    </Button>
                </InputGroup>
            </Form>
            {noneFound && (
                <div id="no-suggestions">
                    <p>Nothing found for: “{query}”</p>
                </div>
            )}
            {!noneFound && showSuggestions && (
                <ul className="suggestion-list" id="suggestion-list">
                    {suggestionData?.pages.map((page, i) => (
                        <React.Fragment key={i}>
                            {page.docs.map((suggestion, di) => (
                                <li
                                    key={suggestion.uid}
                                    role="option"
                                    aria-selected={di === activeIndex}
                                    className={
                                        di === activeIndex ? 'active' : ''
                                    }
                                    onClick={() =>
                                        handleSelectSuggestion(suggestion)
                                    }
                                    onMouseDown={(e) => e.preventDefault()} // Prevents input blur on click
                                    data-uid={suggestion.uid}
                                >
                                    <span className="sa">
                                        {suggestion?.name_deva[0]}
                                    </span>{' '}
                                    <span className="font-times font-125">
                                        ({suggestion.header})
                                    </span>
                                </li>
                            ))}
                        </React.Fragment>
                    ))}
                    {numFound > lastFound && (
                        <li className="text-center">
                            <div>{numFound - lastFound} more entries</div>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    fetchNextPage();
                                    return false;
                                }}
                            >
                                {isFetchingNextPage
                                    ? 'Loading more...'
                                    : hasNextPage
                                    ? 'Load More'
                                    : 'Nothing more to load'}
                            </button>
                            {/* <div>
                                        Numfound: {suggestionData.pages[0].numFound} <br/>
                                        LastPage: {suggestionData.pages[suggestionData.pages.length - 1].start} <br/>
                                    </div> */}
                            <div>
                                {isFetching && !isFetchingNextPage
                                    ? 'Fetching...'
                                    : null}
                            </div>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
